import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  LEGEND_PATTERNS,
  legendPatternFill,
  normalizeLegendHex
} from '../../utils/evmLegend';

/** Colour + hash-pattern picker over a legend square / line. */
export default function LegendColorSwatch({
  color,
  pattern = 'solid',
  onChange,
  onPatternChange,
  hidePicker = false,
  isLine = false,
  dashed = false
}) {
  const hex = normalizeLegendHex(color, '#64748b');
  const fill = legendPatternFill(hex, isLine ? 'solid' : pattern);
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const box = btnRef.current?.getBoundingClientRect();
      if (!box) return;
      const width = 196;
      const left = box.right + 8 + width > window.innerWidth
        ? Math.max(8, box.left - width - 8)
        : box.right + 8;
      const top = Math.min(box.top, window.innerHeight - 220);
      setPos({ top, left });
    };
    place();
    const onDoc = (e) => {
      if (btnRef.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  const canEdit = !hidePicker && (typeof onChange === 'function' || typeof onPatternChange === 'function');

  return (
    <span
      ref={btnRef}
      style={{
        position: 'relative',
        width: isLine ? 16 : 10,
        height: isLine ? 10 : 10,
        flex: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <button
        type="button"
        disabled={!canEdit}
        title={canEdit ? 'Change colour or pattern' : undefined}
        onClick={(e) => {
          e.stopPropagation();
          if (canEdit) setOpen((v) => !v);
        }}
        aria-label="Change colour or pattern"
        style={{
          width: isLine ? 16 : 10,
          height: isLine ? 10 : 10,
          padding: 0,
          border: isLine ? 'none' : '1px solid rgba(15,23,42,0.12)',
          borderRadius: isLine ? 0 : 2,
          cursor: canEdit ? 'pointer' : 'default',
          background: 'transparent',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <span
          aria-hidden
          style={isLine
            ? {
              width: 16,
              height: 0,
              borderTop: dashed ? '2px dashed' : '2.5px solid',
              borderColor: hex,
              display: 'inline-block'
            }
            : {
              width: 10,
              height: 10,
              borderRadius: 2,
              display: 'inline-block',
              ...fill
            }}
        />
      </button>
      {open && canEdit && createPortal(
        <div
          ref={popRef}
          role="dialog"
          aria-label="Legend colour and pattern"
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            zIndex: 4800,
            width: 196,
            padding: 8,
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            boxShadow: '0 10px 28px rgba(15,23,42,0.16)'
          }}
        >
          <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.3 }}>
            Colour
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
            <input
              type="color"
              value={hex}
              title="Change colour"
              onChange={(e) => onChange?.(e.target.value)}
              style={{ width: 36, height: 24, padding: 0, border: '1px solid #cbd5e1', borderRadius: 4, cursor: 'pointer', background: '#fff' }}
            />
            <span style={{ fontSize: 11, color: '#334155', fontFamily: 'ui-monospace, monospace' }}>{hex}</span>
          </div>
          {!isLine && typeof onPatternChange === 'function' && (
            <>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.3, marginTop: 10 }}>
                Pattern
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginTop: 6 }}>
                {LEGEND_PATTERNS.map((p) => {
                  const on = pattern === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      title={p.label}
                      onClick={() => {
                        onPatternChange(p.id);
                        if (p.id !== 'solid' || on) setOpen(false);
                      }}
                      style={{
                        height: 28,
                        borderRadius: 5,
                        border: on ? '2px solid #0f766e' : '1px solid #cbd5e1',
                        cursor: 'pointer',
                        padding: 0,
                        ...legendPatternFill(hex, p.id)
                      }}
                    />
                  );
                })}
              </div>
            </>
          )}
        </div>,
        document.body
      )}
    </span>
  );
}
